import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db, storage } from "../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, setDoc, getDoc, serverTimestamp, collection } from "firebase/firestore";
import { fetchAllCollectionDocs } from "../helpers/quoteVisibility";
import StandUpPouches from "../components/StandUpPouches";
import Boxes from "../components/Boxes";
import Bottles from "../components/NewBottles";
import Caps from "../components/Caps";
import Blisters from "../components/Blisters";
import Header from "../components/Header";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ShrinkSleeves from "../components/ShrinkSleeves";
import Labels from "../components/Labels";
import Bags from "../components/Bags";
import Sachets from "../components/Sachets";
import Other from "../components/Other";
import Envelopes from "../components/Envelopes";
import MailerBags from "../components/MailerBags";
import ConfirmationModal from "../components/ConfirmationModal"; // Import the modal
import { getInvoiceGroupByEmail } from "../helpers/invoiceGroup";
import { blastPartyPaper } from "../helpers/partyPaperCelebration";

const StepOne = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [products, setProducts] = useState([
    {
      productType: '',
      SKU: 0,
      quantitiesCount: 0,
      skuDetails: [],
      fields: {},
      address: '',
      packagingInstructions: '',
      shippingInstructions: '',
      specialNotesToProduction: '',
      imageUrls: [],
      printingStyle: '',
      productionType: ''
    }
  ]);
  const [companyName, setCompanyName] = useState("");
  const [salesRepName, setSalesRepName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [projectId, setProjectId] = useState("");
  const [mainAddress, setMainAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const [projectDescription, setProjectDescription] = useState("");
  const [accountStatus, setAccountStatus] = useState("");
  const [isVIPClient, setIsVIPClient] = useState(false);
  const [invoiceGroup, setInvoiceGroup] = useState("LL");
  const [importMeta, setImportMeta] = useState(null);

  useEffect(() => {
    if (user) {
      const fetchUserName = async () => {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setSalesRepName(userData.name);
          }
        } catch (error) {
          console.error("Error fetching user data: ", error);
        }
      };

      fetchUserName();
      setInvoiceGroup(getInvoiceGroupByEmail(user.email));
    }
  }, [user]);

  useEffect(() => {
    const prefilled = location.state?.prefilledQuote;
    if (!prefilled) return;

    if (prefilled.companyName) setCompanyName(prefilled.companyName);
    if (prefilled.projectName) setProjectName(prefilled.projectName);
    if (prefilled.projectDescription) setProjectDescription(prefilled.projectDescription);
    if (prefilled.accountStatus) setAccountStatus(prefilled.accountStatus);
    if (typeof prefilled.isVIPClient === 'boolean') setIsVIPClient(prefilled.isVIPClient);
    if (prefilled.salesRepName) setSalesRepName(prefilled.salesRepName);
    if (prefilled.products?.length) setProducts(prefilled.products);
    if (location.state?.importMeta) setImportMeta(location.state.importMeta);

    if (prefilled.fromDocumentImport) {
      toast.info('Form pre-filled from your document. Fill in missing fields, then click Submit Quote to save.');
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    setProducts(prev => prev.map(product => ({ ...product, address: mainAddress })));
  }, [mainAddress]);

  const handleProductChange = (index, field, value) => {
    const newProducts = [...products];
    newProducts[index][field] = value;

    if (field === 'SKU' || field === 'quantitiesCount') {
      const skus = newProducts[index].SKU;
      const quantities = newProducts[index].quantitiesCount;

      newProducts[index].skuDetails = Array.from({ length: skus }, (_, skuIndex) => ({
        sku: skuIndex + 1,
        quantities: Array.from({ length: quantities }, (_, qtyIndex) => ({
          quantity: qtyIndex + 1,
          value: newProducts[index].skuDetails?.[skuIndex]?.quantities?.[qtyIndex]?.value || ""
        }))
      }));
    }

    setProducts(newProducts);
  };

  const incrementValue = (index, field) => {
    const currentValue = products[index][field];
    handleProductChange(index, field, currentValue + 1);
  };

  const decrementValue = (index, field) => {
    const currentValue = products[index][field];
    if (currentValue > 1) {
      handleProductChange(index, field, currentValue - 1);
    }
  };

  const copySKUValues = (index, sourceSKUIndex) => {
    const newProducts = [...products];
    const sourceQuantities = newProducts[index].skuDetails[sourceSKUIndex].quantities;
    
    // Copy values to all other SKUs
    newProducts[index].skuDetails.forEach((sku, skuIndex) => {
      if (skuIndex !== sourceSKUIndex) {
        sku.quantities = sourceQuantities.map(qty => ({
          quantity: qty.quantity,
          value: qty.value
        }));
      }
    });
    
    setProducts(newProducts);
  };

  const handleSKUQuantityChange = (index, skuIndex, qtyIndex, value) => {
    const newProducts = [...products];
    newProducts[index].skuDetails[skuIndex].quantities[qtyIndex].value = parseInt(value) || "";
    setProducts(newProducts);
  };

  const handlePrintingStyleChange = (index, value) => {
    const newProducts = [...products];
    newProducts[index].printingStyle = value;
    setProducts(newProducts);
  };
  

  const handleFieldChange = useCallback((index, field, value) => {
    const newProducts = [...products];
    if (!newProducts[index].fields) {
      newProducts[index].fields = {};
    }
    
    // Update field directly for packaging and shipping instructions
    if (field === "packagingInstructions" || 
        field === "shippingInstructions" || 
        field === "specialNotesToProduction" || 
        field === "previousInvoiceNumber" || 
        field === "colorMatchDetails" ||
        field === "colorMatchOrderNumber") {
      newProducts[index][field] = value;
    } else {
      newProducts[index].fields[field] = value;
    }

    setProducts(newProducts);
  }, [products]);

  const handleFileChange = async (e, index, field) => {
    const files = Array.from(e.target.files);
    const newProducts = [...products];
    
    // Upload each file and get URLs
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `quotes/${projectId}/${file.name}`);
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    });

    try {
      const urls = await Promise.all(uploadPromises);
      if (field === 'colorMatchArtwork') {
        newProducts[index].colorMatchArtworkUrls = [...(newProducts[index].colorMatchArtworkUrls || []), ...urls];
      } else if (field === 'referenceImage') {
        // Handle reference image for Other component
        newProducts[index].fields.referenceImageUrl = urls[0];
      } else {
        newProducts[index][field] = urls[0]; // For other single file uploads
      }
      setProducts(newProducts);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload some files');
    }
  };

  const handleImageChange = async (e, index) => {
    const files = Array.from(e.target.files);
    const newProducts = [...products];
    
    // Upload each file and get URLs
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `quotes/${projectId}/${file.name}`);
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    });

    try {
      const urls = await Promise.all(uploadPromises);
      newProducts[index].imageUrls = [...(newProducts[index].imageUrls || []), ...urls];
      setProducts(newProducts);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload some images');
    }
  };

  const removeImage = (index, imageIndex) => {
    const newProducts = [...products];
    newProducts[index].imageUrls.splice(imageIndex, 1);
    setProducts(newProducts);
  };

  const addProduct = () => {
    setProducts([...products, {
      productType: '',
      SKU: 1,
      quantitiesCount: 1,
      skuDetails: [],
      fields: {},
      address: mainAddress,
      packagingInstructions: '',
      shippingInstructions: '',
      imageUrls: [],
      printingStyle: '',
      productionType: ''
    }]);
  };

  const removeProduct = (index) => {
    const newProducts = products.filter((_, i) => i !== index);
    setProducts(newProducts);
  };

  const generateUniqueProjectId = useCallback(async (prefix = invoiceGroup) => {
    const baseId = prefix;
    let idNumber = 1;
    let newProjectId = `${baseId}${idNumber.toString().padStart(3, "0")}`;

    const projectsCollection = collection(db, "QuoteRequirements");
    const existingProjectIds = await fetchAllCollectionDocs(projectsCollection);

    const existingIds = existingProjectIds
      .map((entry) => entry.data().projectId)
      .filter((id) => typeof id === "string" && id.startsWith(baseId));

    while (existingIds.includes(newProjectId)) {
      idNumber++;
      newProjectId = `${baseId}${idNumber.toString().padStart(3, "0")}`;
    }

    return newProjectId;
  }, [invoiceGroup]);

  const notifyAna = async (quoteId, subject, textBody, htmlBody) => {
    try {
      const response = await fetch('https://ghft6mowc4.execute-api.us-east-2.amazonaws.com/default/QuoteForm-EmailSender', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipientEmail: 'ana@labelslab.com',
          ccEmail: 'alejandra@labelslab.com, claudia@labelslab.com, fernandaac@labelslab.com',
          subject: subject,
          textBody: textBody,
          htmlBody: htmlBody
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to send email: ${response.status} ${response.statusText}. ${errorText}`);
      }

      console.log('Email sent successfully to Ana.');
    } catch (error) {
      console.error('Error notifying Admin:', error);
      // Optionally show a toast notification to the user
      toast.error('Failed to send notification email. The quote was saved successfully.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!companyName || !salesRepName || !products[0].productType) {
      toast.error("Please fill in all required fields.");
      return;
    }

    // Validate mandatory fields for each product
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      const productNumber = i + 1;
      
      // Check quantity validation (skip for Other products)
      if (product.productType !== 'Other') {
        const hasValidQuantity = product.skuDetails && product.skuDetails.length > 0 && 
          product.skuDetails.every(sku => 
            sku.quantities && sku.quantities.length > 0 && 
            sku.quantities.every(qty => qty.value && qty.value > 0)
          );
        
        if (!hasValidQuantity) {
          toast.error(`Product ${productNumber}: Please fill in all quantity values.`);
          return;
        }
      }

      // Check size validation based on product type (skip for Other products)
      if (product.productType !== 'Other') {
        let hasValidSize = false;
        if (product.productType === 'Bags') {
          hasValidSize = product.fields.width && product.fields.height &&
            (product.fields.width > 0) && (product.fields.height > 0);
        } else if (product.productType === 'Boxes') {
          // Cylinder Box (Round Box) - option 5 - only needs width and height, no depth
          if (product.fields.boxType === '5') {
            hasValidSize = product.fields.height && product.fields.width &&
              (product.fields.height > 0) && (product.fields.width > 0);
          } else {
            hasValidSize = product.fields.height && product.fields.width && product.fields.depth &&
              (product.fields.height > 0) && (product.fields.width > 0) && (product.fields.depth > 0);
          }
        } else if (['ShrinkSleeves', 'StandUpPouches', 'Labels'].includes(product.productType)) {
          hasValidSize = product.fields.size && product.fields.size.heightMM && product.fields.size.widthMM &&
            (product.fields.size.heightMM > 0) && (product.fields.size.widthMM > 0);
        } else if (product.productType === 'Bottles') {
          hasValidSize = product.fields.size && product.fields.size.trim() !== '';
        } else if (['Envelopes', 'MailerBags'].includes(product.productType)) {
          // Envelopes/MailerBags use width and height directly
          hasValidSize = product.fields.width && product.fields.height &&
            (product.fields.width > 0) && (product.fields.height > 0);
        } else if (['Blisters', 'Caps', 'Sachets'].includes(product.productType)) {
          // Blisters, Caps, Sachets use size object with widthMM/heightMM
          hasValidSize = product.fields.size && product.fields.size.widthMM && product.fields.size.heightMM &&
            (product.fields.size.widthMM > 0) && (product.fields.size.heightMM > 0);
        } else {
          hasValidSize = product.fields.size && Object.keys(product.fields.size || {}).length > 0;
        }
        
        if (!hasValidSize) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[Validation] Size check failed:', { productType: product.productType, fields: product.fields });
          }
          toast.error(`Product ${productNumber} (${product.productType}): Please fill in all size dimensions.`);
          return;
        }
      }

      // Check material validation based on product type (skip for Other products)
      if (product.productType !== 'Other') {
        let hasValidMaterial = false;
        if (product.productType === 'Bags') {
          hasValidMaterial = product.fields.material && product.fields.material.trim() !== '';
        } else if (product.productType === 'Boxes') {
          hasValidMaterial = product.fields.material && product.fields.material.trim() !== '';
        } else if (product.productType === 'ShrinkSleeves') {
          hasValidMaterial = product.fields.material && product.fields.material.trim() !== '';
        } else if (product.productType === 'StandUpPouches') {
          hasValidMaterial = product.fields.material && product.fields.material.trim() !== '';
        } else if (product.productType === 'Labels') {
          hasValidMaterial = product.fields.material && product.fields.material.trim() !== '';
        } else if (product.productType === 'Bottles') {
          hasValidMaterial = product.fields.materialGroup && product.fields.materialGroup.trim() !== '';
        } else if (product.productType === 'Caps') {
          // Caps: uses capType, no material field
          hasValidMaterial = product.fields.capType && product.fields.capType.trim() !== '';
        } else if (['Blisters', 'Sachets', 'Envelopes', 'MailerBags'].includes(product.productType)) {
          hasValidMaterial = product.fields.material && product.fields.material.trim() !== '';
        } else {
          hasValidMaterial = product.fields.material && product.fields.material.trim() !== '';
        }
        
        if (!hasValidMaterial) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[Validation] Material check failed:', { productType: product.productType, fields: product.fields });
          }
          toast.error(`Product ${productNumber} (${product.productType}): Please select a material.`);
          return;
        }
      }

      // Check finish validation based on product type (skip for Other products)
      if (product.productType !== 'Other') {
        let hasValidFinish = false;
        if (product.productType === 'Bags') {
          hasValidFinish = product.fields.printingFinishes && product.fields.printingFinishes.trim() !== '';
        } else if (product.productType === 'Boxes') {
          hasValidFinish = product.fields.printingFinishes && product.fields.printingFinishes.trim() !== '';
        } else if (product.productType === 'ShrinkSleeves') {
          hasValidFinish = product.fields.finishes && product.fields.finishes.trim() !== '';
        } else if (product.productType === 'StandUpPouches') {
          hasValidFinish = (product.fields.finishType && product.fields.finishType.trim() !== '') ||
                          (product.fields.finishOption && product.fields.finishOption.trim() !== '');
        } else if (product.productType === 'Labels') {
          hasValidFinish = product.fields.finish && product.fields.finish.trim() !== '';
        } else if (product.productType === 'Blisters') {
          // Blisters: optional checkbox finishings - skip finish validation
          hasValidFinish = true;
        } else if (product.productType === 'Caps') {
          // Caps: no finish field - skip finish validation
          hasValidFinish = true;
        } else if (product.productType === 'Sachets') {
          // Sachets: uses specialFinishes (array) - optional checkboxes
          hasValidFinish = true;
        } else if (product.productType === 'Envelopes' || product.productType === 'MailerBags') {
          hasValidFinish = product.fields.printingFinishes && product.fields.printingFinishes.trim() !== '';
        } else if (product.productType === 'Bottles') {
          hasValidFinish = (product.fields.finishType && product.fields.finishType.trim() !== '') ||
                          (product.fields.finishOption && product.fields.finishOption.trim() !== '');
        } else {
          // Fallback for any other product types
          hasValidFinish = product.fields.finish || product.fields.printingFinishes ||
                          product.fields.finishType || product.fields.finishOption;
        }

        if (!hasValidFinish) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[Validation] Finish check failed:', { productType: product.productType, fields: product.fields });
          }
          toast.error(`Product ${productNumber} (${product.productType}): Please select a finish option.`);
          return;
        }
      }
    }

    // Debug: Check for File objects before submission
    const checkForFileObjects = (obj, path = '') => {
      for (const key in obj) {
        if (obj[key] instanceof File) {
          console.error(`File object found at ${path}.${key}:`, obj[key]);
          return true;
        }
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          if (checkForFileObjects(obj[key], `${path}.${key}`)) {
            return true;
          }
        }
      }
      return false;
    };

    if (checkForFileObjects(products, 'products')) {
      toast.error("Please wait for file uploads to complete before submitting.");
      return;
    }

    // Clean up any File objects that might be in the data
    const cleanProducts = products.map(product => {
      const cleanedProduct = { ...product };
      
      // Clean fields object
      if (cleanedProduct.fields) {
        cleanedProduct.fields = { ...cleanedProduct.fields };
        Object.keys(cleanedProduct.fields).forEach(key => {
          if (cleanedProduct.fields[key] instanceof File) {
            console.warn(`Removing File object from fields.${key}`);
            delete cleanedProduct.fields[key];
          }
        });
      }
      
      // Clean top-level product properties
      Object.keys(cleanedProduct).forEach(key => {
        if (cleanedProduct[key] instanceof File) {
          console.warn(`Removing File object from product.${key}`);
          delete cleanedProduct[key];
        }
      });
      
      return cleanedProduct;
    });

    // Check if Color Match is "Yes" but Order Number is missing
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      if (product.colorMatch === "Yes" && (!product.colorMatchOrderNumber || product.colorMatchOrderNumber.trim() === "")) {
        toast.error(`Product ${i + 1}: Order Number is required when Color Match is "Yes".`);
        return;
      }
    }

    // Check if Reprint is "Yes" but Previous Invoice Number is missing
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      if (product.isReprint === "Yes" && (!product.previousInvoiceNumber || product.previousInvoiceNumber.trim() === "")) {
        toast.error(`Product ${i + 1}: Previous Invoice Number is required when "Is this a Reprint?" is "Yes".`);
        return;
      }
    }

    setIsLoading(true);

    let uniqueProjectId = projectId;
    let isUnique = false;

    while (!isUnique) {
      const docRef = doc(db, "QuoteRequirements", uniqueProjectId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        isUnique = true;
      } else {
        uniqueProjectId = await generateUniqueProjectId();
      }
    }

    const quoteData = {
      createdBy: user.uid,
      createdOn: serverTimestamp(),
      invoiceGroup,
      companyName,
      salesRepName,
      projectName,
      projectId: uniqueProjectId,
      projectDescription,
      accountStatus,
      isVIPClient,
      products: cleanProducts,
      ...(importMeta ? { source: 'document-import' } : {}),
    };

    try {
      await setDoc(doc(db, "QuoteRequirements", uniqueProjectId), quoteData);
      blastPartyPaper();
      toast.success(`Quote ${uniqueProjectId} submitted! Find it under Packaging Details.`);

      const subject = 'New Quote Submitted';
      const textBody = `A new quote has been submitted. View it at https://shipping-quote.labelslab.com/packaging-details/${uniqueProjectId}`;
      const htmlBody = `
        <div style="font-family: Arial, sans-serif; margin: 20px;">
          <h2 style="color: #333;">New Quote Submitted</h2>
          <p style="font-size: 16px; color: #555;">
            A new quote has been submitted. Click the button below to view the details:
          </p>
          <a href="https://shipping-quote.labelslab.com/packaging-details/${uniqueProjectId}" style="display: inline-block; margin-top: 20px; padding: 10px 20px; color: white; background-color: #007BFF; text-decoration: none; border-radius: 5px;">
            View the Quote
          </a>
          <p style="font-size: 14px; color: #999; margin-top: 20px;">
            If you have any questions, please contact us at <a href="mailto:vaibhav@designersprescription.com" style="color: #007BFF;">vaibhav@designersprescription.com</a>.
          </p>
        </div>
      `;

      await notifyAna(uniqueProjectId, subject, textBody, htmlBody);
      setImportMeta(null);
      setTimeout(() => {
        setCompanyName("");
        setSalesRepName("");
        setProjectName("");
        setProjectDescription("");
        setAccountStatus("");
        setIsVIPClient(false);
        setProducts([{
          productType: '',
          SKU: 1,
          quantitiesCount: 1,
          skuDetails: [],
          fields: {},
          address: '',
          packagingInstructions: '',
          shippingInstructions: '',
          imageUrls: [],
          printingStyle: '',
          productionType: ''
        }]);
        generateUniqueProjectId(invoiceGroup).then((nextProjectId) => {
          setProjectId(nextProjectId);
        });
        setMainAddress("");
        setIsLoading(false);
      }, 2000);
    } catch (error) {
      console.error(error.message);
      toast.error("Failed to submit the form.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    const initializeProjectId = async () => {
      const newProjectId = await generateUniqueProjectId();
      setProjectId(newProjectId);
    };

    initializeProjectId();
  }, [user, generateUniqueProjectId]);

  const productComponents = {
    StandUpPouches,
    Boxes,
    Bottles,
    Caps,
    Blisters,
    ShrinkSleeves,
    Labels,
    Bags,
    Sachets,
    Other,
    Envelopes,
    MailerBags,
  };

  const handleColorMatchChange = (index, value) => {
    const newProducts = [...products];
    newProducts[index].colorMatch = value;
    setProducts(newProducts);
  };

  const handleReprintChange = (index, value) => {
    const newProducts = [...products];
    newProducts[index].isReprint = value;
    setProducts(newProducts);
  };

  const handleProductionTypeChange = (index, value) => {
    const newProducts = [...products];
    newProducts[index].productionType = value;
    setProducts(newProducts);
  };

  const removeColorMatchArtwork = (index, imageIndex) => {
    const newProducts = [...products];
    newProducts[index].colorMatchArtworkUrls.splice(imageIndex, 1);
    setProducts(newProducts);
  };

  return (
    <>
      <Header />
      <div className="parent-container pt-5 h-100 pb-10 p-4 sm:ml-64">
        <div className="container mx-auto px-4 py-8">
          <h2 className="text-3xl p-10 mb-6" style={{ textAlign: "center" }}>
            Step One: Quote Requirements
          </h2>

          {importMeta && (
            <div className="max-w-4xl mx-auto mb-6 space-y-3">
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                <p className="text-sm font-semibold text-blue-900 mb-1">Imported from document</p>
                {importMeta.summary && (
                  <p className="text-sm text-blue-800">{importMeta.summary}</p>
                )}
                {importMeta.missingFields?.length > 0 && (
                  <p className="text-sm text-orange-800 mt-2">
                    Still needed: {importMeta.missingFields.join(', ')}
                  </p>
                )}
                <p className="text-xs text-blue-700 mt-2">
                  Product fields are pre-filled below. Complete missing details, then click Submit Quote to save.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={(e) => e.preventDefault()}>
            {/* Customer Details Section */}
            <div className="mb-4">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Company Name: <span className="text-red-500">*</span></label>
              <input
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Company Name"
                required
              />
            </div>
            {/* Sales Rep Name */}
            <div className="mb-4">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Sales Rep Name:</label>
              <select
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                value={salesRepName}
                onChange={(e) => setSalesRepName(e.target.value)}
                required
              >
<option value="">Select Sales Rep</option>
    <optgroup label="Sales Reps">
        <option value="Ilse">Ilse</option>
        <option value="Sandra">Sandra</option>
        <option value="Francisco">Francisco</option>
        <option value="Aldair">Aldair</option>
        <option value="Alfonso">Alfonso</option>
        <option value="Maria">Maria</option>
        <option value="Marlen">Marlen</option>
        <option value="Ana Karen">Ana Karen</option>
        <option value="Aylin">Aylin</option>
        <option value="Pamela">Pamela</option>
        <option value="Carolina">Carolina</option>
        <option value="Fernando">Fernando</option>
        <option value="German">German</option>
        <option value="Andrey">Andrey</option>
        <option value="Callie">Callie</option>
        <option value="Lyn">Lyn</option>
        <option value="Daniela">Daniela</option>
        <option value="Joe">Joe</option>
        <option value="Sabastian">Sabastian</option>
                  
    </optgroup>
    <optgroup label="Admins">
        <option value="Lorena">Lorena</option>
        <option value="Colin">Colin</option>
        <option value="Em">Em</option>
        <option value="Pinkel">Pinkel</option>
        <option value="Jane">Jane</option>
    </optgroup>

              </select>
            </div>
            {/* Project Details */}
            <div className="mb-4">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Project Name: <span className="text-red-500">*</span></label>
              <input
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Project Name"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Project ID:</label>
              <input
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                type="text"
                value={projectId}
                readOnly
                placeholder="Project ID"
              />
              <small className="text-gray-500 text-xs">
                Project ID is auto-generated and cannot be changed. Prefix for your account: {invoiceGroup}
              </small>
            </div>

            {/* Short Description Quote Box */}
            <div className="mb-4">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Short Description Quote Detail: <span className="text-red-500">*</span></label>
              <textarea
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                rows="3"
                placeholder="Products Number, Sizes, How many materials per product and SKU's"
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                required
              />
            </div>

            {/* Account Status */}
            <div className="mb-4">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Account Status:</label>
              <select
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                value={accountStatus}
                onChange={(e) => setAccountStatus(e.target.value)}
              >
                <option value="">Select Account Status</option>
                <option value="Bronze">Bronze</option>
                <option value="Silver">Silver</option>
                <option value="Gold">Gold</option>
                <option value="Platinum">Platinum</option>
              </select>
            </div>

            {/* VIP Client */}
            <div className="mb-6">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900 mb-2">VIP Client:</label>
              <label className="flex items-center gap-3 cursor-pointer w-fit">
                <input
                  type="checkbox"
                  checked={isVIPClient}
                  onChange={(e) => setIsVIPClient(e.target.checked)}
                  className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded cursor-pointer"
                />
                <span className="text-sm text-gray-700 font-medium">Yes, this is a VIP Client</span>
              </label>
            </div>

            {/* Products Section */}
            {products.map((product, index) => (
              <div key={index} className="mb-8 p-6 bg-white rounded-lg shadow-md">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Product {index + 1}</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                      onClick={() => removeProduct(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                
                {/* Product Type Selection */}
                <div className="mb-4">
                  <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Product Type:</label>
                  <select
                    className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 ${product.productType ? 'cursor-not-allowed opacity-70' : ''}`}
                    value={product.productType}
                    onChange={(e) => handleProductChange(index, "productType", e.target.value)}
                    disabled={product.productType !== ''}
                  >
                    <option value="">Select Product Type</option>
                    <option value="Boxes">Boxes</option>
                    <option value="Bags">Bags</option>
                    <option value="Blisters">Blisters</option>
                    <option value="Bottles">Bottles</option>
                    <option value="Caps">Caps</option>
                    <option value="Labels">Labels</option>
                    <option value="ShrinkSleeves">Shrink Sleeves</option>
                    <option value="Sachets">Sachets</option>
                    <option value="Envelopes">Envelopes</option>
                    <option value="MailerBags">Mailer Bags</option>
                    <option value="Other">Other</option>
                  </select>
                  {product.productType && (
                    <p className="mt-2 text-sm text-red-600">
                      Note: Product type cannot be changed once selected. To change product type, please delete this product and add a new one.
                    </p>
                  )}
                </div>

                {/* Dynamic Product Component Rendering */}
                {product.productType && productComponents[product.productType] && (
                  <div className="mb-4">
                    <h4 className="text-md font-semibold mb-2">Custom Fields for {product.productType}</h4>
                    {React.createElement(productComponents[product.productType], {
                      key: `${product.productType}-${index}-${product.fields?.bagType || product.fields?.boxType || ''}`,
                      product: product,
                      updateProduct: (field, value) => handleFieldChange(index, field, value),
                      handleFileChange: (e) => handleFileChange(e, index, e.target.name),
                      handleInputChange: (e) => handleFieldChange(index, e.target.name, e.target.value),
                    })}
                  </div>
                )}

                {/* Packaging Instructions */}
                <div className="mb-4">
                  <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Special Notes to Production:</label>
                  <textarea
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    rows="4"
                    value={product.specialNotesToProduction}
                    onChange={(e) => handleFieldChange(index, "specialNotesToProduction", e.target.value)}
                    placeholder="Special Notes"
                  />
                </div>


                <div className="mb-4">
  <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Printing Style: <span className="text-red-500">*</span></label>
  <div className="flex items-center space-x-4 mt-2">
  {/* // Add a new text field label for "Other" When PMS is selected to type in the number of Pantone Colors*/}
    <label className="flex items-center">
      <input
        type="radio"
        name={`printingStyle-${index}`}
        value="PMS"
        checked={product.printingStyle === "PMS"}
        onChange={() => handlePrintingStyleChange(index, "PMS")}
        className="form-radio text-blue-500"
        required
      />
      <span className="ml-2">PMS</span>
    </label>
    <label className="flex items-center">
      <input
        type="radio"
        name={`printingStyle-${index}`}
        value="CMYK"
        checked={product.printingStyle === "CMYK"}
        onChange={() => handlePrintingStyleChange(index, "CMYK")}
        className="form-radio text-blue-500"
        required
      />
      <span className="ml-2">CMYK</span>
    </label>
    <label className="flex items-center">
      <input
        type="radio"
        name={`printingStyle-${index}`}
        value="Both"
        checked={product.printingStyle === "Both"}
        onChange={() => handlePrintingStyleChange(index, "Both")}
        className="form-radio text-blue-500"
        required
      />
      <span className="ml-2">Both</span>
    </label>
    <label className="flex items-center">
      <input
        type="radio"
        name={`printingStyle-${index}`}
        value="N/A"
        checked={product.printingStyle === "N/A"}
        onChange={() => handlePrintingStyleChange(index, "N/A")}
        className="form-radio text-blue-500"
        required
      />
      <span className="ml-2">N/A</span>
    </label>
  </div>
</div>

  {/* Production Type - New Section */}
  <div className="mb-4">
    <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Production Type: <span className="text-red-500">*</span></label>
    <div className="flex items-center space-x-4 mt-2">
      <label className="flex items-center">
        <input
          type="radio"
          name={`productionType-${index}`}
          value="Full Run"
          checked={product.productionType === "Full Run"}
          onChange={() => handleProductionTypeChange(index, "Full Run")}
          className="form-radio text-blue-500"
          required
        />
        <span className="ml-2">Full Run</span>
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          name={`productionType-${index}`}
          value="Sample Run"
          checked={product.productionType === "Sample Run"}
          onChange={() => handleProductionTypeChange(index, "Sample Run")}
          className="form-radio text-blue-500"
          required
        />
        <span className="ml-2">Sample Run</span>
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          name={`productionType-${index}`}
          value="Full Run and Sample Run both"
          checked={product.productionType === "Full Run and Sample Run both"}
          onChange={() => handleProductionTypeChange(index, "Full Run and Sample Run both")}
          className="form-radio text-blue-500"
          required
        />
        <span className="ml-2">Full Run and Sample Run both</span>
      </label>
    </div>
  </div>

                {/* Product Image Upload */}
                <div className="mb-4">
                  <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Product Images & Files:</label>
                  <input
                    type="file"
                    accept="image/*,.pdf,.ai"
                    multiple
                    onChange={(e) => handleImageChange(e, index)}
                    className="mb-2"
                  />
                  <p className="text-sm text-gray-600 mb-2">Supported formats: Images (JPG, PNG, GIF), PDF files, AI files</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2">
                    {product.imageUrls?.map((url, imageIndex) => {
                      const fileName = url.split('/').pop() || '';
                      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
                      const isPDF = /\.pdf$/i.test(fileName);
                      const isAI = /\.ai$/i.test(fileName);
                      
                      return (
                        <div key={imageIndex} className="relative">
                          {isImage ? (
                            <img 
                              src={url} 
                              alt={`Product ${index + 1}, file ${imageIndex + 1}`} 
                              className="w-full h-32 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                              {isPDF ? (
                                <div className="text-center">
                                  <svg className="w-12 h-12 text-red-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                  </svg>
                                  <p className="text-xs text-gray-600">PDF</p>
                                </div>
                              ) : isAI ? (
                                <div className="text-center">
                                  <svg className="w-12 h-12 text-orange-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                  </svg>
                                  <p className="text-xs text-gray-600">AI</p>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <svg className="w-12 h-12 text-gray-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                  </svg>
                                  <p className="text-xs text-gray-600">File</p>
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(index, imageIndex)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* SKU and Quantities Configuration */}
                <div className="mb-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">
                        SKU Count: {product.productType !== 'Other' && <span className="text-red-500">*</span>}
                      </label>
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => decrementValue(index, "SKU")}
                          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-l-lg"
                        >
                          -
                        </button>
                        <input
                          className="bg-gray-50 border-y border-gray-300 text-gray-900 text-sm text-center w-20 p-2.5 focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          type="number"
                          name="SKU"
                          value={product.SKU}
                          onChange={(e) => handleProductChange(index, "SKU", Math.max(1, parseInt(e.target.value) || 1))}
                          min="1"
                          required={product.productType !== 'Other'}
                        />
                        <button
                          type="button"
                          onClick={() => incrementValue(index, "SKU")}
                          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-r-lg"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">
                        Quantities Per SKU: {product.productType !== 'Other' && <span className="text-red-500">*</span>}
                      </label>
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => decrementValue(index, "quantitiesCount")}
                          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-l-lg"
                        >
                          -
                        </button>
                        <input
                          className="bg-gray-50 border-y border-gray-300 text-gray-900 text-sm text-center w-20 p-2.5 focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          type="number"
                          name="quantitiesCount"
                          value={product.quantitiesCount}
                          onChange={(e) => handleProductChange(index, "quantitiesCount", Math.max(1, parseInt(e.target.value) || 1))}
                          min="1"
                          required={product.productType !== 'Other'}
                        />
                        <button
                          type="button"
                          onClick={() => incrementValue(index, "quantitiesCount")}
                          className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-r-lg"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* SKU Details Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-4 py-2 border-b">SKU</th>
                          {product.skuDetails?.[0]?.quantities.map((_, qtyIndex) => (
                            <th key={qtyIndex} className="px-4 py-2 border-b">Quantity {qtyIndex + 1}</th>
                          ))}
                          <th className="px-4 py-2 border-b">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.skuDetails?.map((sku, skuIndex) => (
                          <tr key={skuIndex} className={skuIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-2 border-b font-medium">SKU {sku.sku}</td>
                            {sku.quantities.map((quantity, qtyIndex) => (
                              <td key={qtyIndex} className="px-4 py-2 border-b">
                                <input
                                  className="bg-transparent border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  type="number"
                                  value={quantity.value}
                                  onChange={(e) => handleSKUQuantityChange(index, skuIndex, qtyIndex, e.target.value)}
                                  placeholder={`Qty ${qtyIndex + 1}`}
                                />
                              </td>
                            ))}
                            <td className="px-4 py-2 border-b">
                              {skuIndex > 0 && (
                                <button
                                  type="button"
                                  onClick={() => copySKUValues(index, 0)}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  Copy from SKU 1
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Color Match Option */}
                <div className="mb-4">
                  <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Color Match: <span className="text-red-500">*</span></label>
                  <div className="flex items-center space-x-4 mt-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`colorMatch-${index}`}
                        value="Yes"
                        checked={product.colorMatch === "Yes"}
                        onChange={() => handleColorMatchChange(index, "Yes")}
                        className="form-radio text-blue-500"
                        required
                      />
                      <span className="ml-2">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`colorMatch-${index}`}
                        value="No"
                        checked={product.colorMatch === "No"}
                        onChange={() => handleColorMatchChange(index, "No")}
                        className="form-radio text-blue-500"
                        required
                      />
                      <span className="ml-2">No</span>
                    </label>
                  </div>

                  {/* Moved Color Match Artwork and Notes here */}
                  {product.colorMatch === "Yes" && (
                    <div className="mt-4">
                      <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Upload Artwork for Color Match:</label>
                      <input
                        type="file"
                        accept="image/*,.pdf,.ai"
                        multiple
                        onChange={(e) => handleFileChange(e, index, 'colorMatchArtwork')}
                        className="mb-2"
                      />
                      <p className="text-sm text-gray-600 mb-2">Supported formats: Images (JPG, PNG, GIF), PDF files, AI files</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-2">
                        {product.colorMatchArtworkUrls?.map((url, imageIndex) => {
                          const fileName = url.split('/').pop() || '';
                          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
                          const isPDF = /\.pdf$/i.test(fileName);
                          const isAI = /\.ai$/i.test(fileName);
                          
                          return (
                            <div key={imageIndex} className="relative">
                              {isImage ? (
                                <img 
                                  src={url} 
                                  alt={`Color Match Artwork ${imageIndex + 1}`} 
                                  className="w-full h-32 object-cover rounded-lg"
                                />
                              ) : (
                                <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                                  {isPDF ? (
                                    <div className="text-center">
                                      <svg className="w-12 h-12 text-red-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                      </svg>
                                      <p className="text-xs text-gray-600">PDF</p>
                                    </div>
                                  ) : isAI ? (
                                    <div className="text-center">
                                      <svg className="w-12 h-12 text-orange-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                      </svg>
                                      <p className="text-xs text-gray-600">AI</p>
                                    </div>
                                  ) : (
                                    <div className="text-center">
                                      <svg className="w-12 h-12 text-gray-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                      </svg>
                                      <p className="text-xs text-gray-600">File</p>
                                    </div>
                                  )}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => removeColorMatchArtwork(index, imageIndex)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      
                      <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900 mt-4">Order Number: <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        value={product.colorMatchOrderNumber || ''}
                        onChange={(e) => handleFieldChange(index, "colorMatchOrderNumber", e.target.value)}
                        placeholder="Enter the order number for color match reference"
                        required
                      />

                      <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900 mt-4">Color Match Details:</label>
                      <textarea
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        rows="4"
                        value={product.colorMatchDetails || ''}
                        onChange={(e) => handleFieldChange(index, "colorMatchDetails", e.target.value)}
                        placeholder="Drop files here or write notes about the reference for color match."
                      />
                    </div>
                  )}
                </div>

                {/* Reprint Option */}
                <div className="mb-4">
                  <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Is this a Reprint? <span className="text-red-500">*</span></label>
                  <div className="flex items-center space-x-4 mt-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`isReprint-${index}`}
                        value="Yes"
                        checked={product.isReprint === "Yes"}
                        onChange={() => handleReprintChange(index, "Yes")}
                        className="form-radio text-blue-500"
                        required
                      />
                      <span className="ml-2">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name={`isReprint-${index}`}
                        value="No"
                        checked={product.isReprint === "No"}
                        onChange={() => handleReprintChange(index, "No")}
                        className="form-radio text-blue-500"
                        required
                      />
                      <span className="ml-2">No</span>
                    </label>
                  </div>
                </div>

                {/* Previous Invoice Number if Reprint is Yes */}
                {product.isReprint === "Yes" && (
                  <div className="mb-4">
                    <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Previous Invoice Number: <span className="text-red-500">*</span></label>
                    <input
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      type="text"
                      value={product.previousInvoiceNumber || ''}
                      onChange={(e) => handleFieldChange(index, "previousInvoiceNumber", e.target.value)}
                      placeholder="Enter Previous Invoice Number"
                      required
                    />
                  </div>
                )}

              </div>
            ))}

            {/* Add Product Button */}
            <div className="mb-6">
              <button
                type="button"
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md bg-green-500 text-white hover:bg-green-700"
                onClick={addProduct}
              >
                Add Another Product
              </button>
            </div>

            {/* Preview and Submit Buttons */}
            <button
              type="button"
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md bg-yellow-500 text-white hover:bg-yellow-600 mr-2"
              onClick={() => setIsModalOpen(true)}
            >
              Preview Quote
            </button>

            <button
              className="mt-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md bg-black text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-light"
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit Quote"}
            </button>
          </form>
          <ToastContainer position="top-center" autoClose={9000} />
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        quoteData={{ companyName, salesRepName, projectName, projectId, projectDescription, products }}
      />
    </>
  );
};

export default StepOne;
